import React, { useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Mail, Send, MessageSquare, Clock, ShieldCheck, Loader2, CheckCircle2, Instagram } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: 'Question', message: '', website: '' });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const subjects = [
    'Question sur l\'abonnement',
    'Problème technique',
    'Demande de fonctionnalité',
    'Presse / Partenariat',
    'Programme d\'affiliation',
    'Autre',
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: '', email: '', subject: 'Question', message: '', website: '' });
      toast.success('Message envoyé — nous répondons sous 24h ouvrées');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur envoi du message');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-8" data-testid="contact-page">
      <div className="text-center space-y-3">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10">
          <MessageSquare className="w-7 h-7 text-primary" />
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold">Nous contacter</h1>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          Une question, un bug, une idée ? Écrivez-nous. Nous répondons personnellement, en moins de 24h ouvrées.
        </p>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <Card className="p-4 flex items-start gap-3">
          <Clock className="w-5 h-5 text-emerald-400 mt-0.5" />
          <div>
            <div className="text-sm font-semibold">Réponse rapide</div>
            <div className="text-xs text-muted-foreground">Moins de 24h en semaine, 48h le week-end</div>
          </div>
        </Card>
        <Card className="p-4 flex items-start gap-3">
          <Mail className="w-5 h-5 text-emerald-400 mt-0.5" />
          <div>
            <div className="text-sm font-semibold">Contact direct</div>
            <div className="text-xs text-muted-foreground">
              <a href="mailto:contact@tradex-ai.fr" className="text-primary hover:underline">contact@tradex-ai.fr</a>
            </div>
          </div>
        </Card>
        <Card className="p-4 flex items-start gap-3">
          <Instagram className="w-5 h-5 text-emerald-400 mt-0.5" />
          <div>
            <div className="text-sm font-semibold">Instagram</div>
            <div className="text-xs text-muted-foreground">
              <a
                href="https://www.instagram.com/tradexai.fr/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
                data-testid="contact-instagram-link"
              >@tradexai.fr</a>
            </div>
          </div>
        </Card>
        <Card className="p-4 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-400 mt-0.5" />
          <div>
            <div className="text-sm font-semibold">Confidentiel</div>
            <div className="text-xs text-muted-foreground">Vos messages ne sont jamais partagés</div>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        {sent ? (
          <div className="text-center py-8 space-y-3" data-testid="contact-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
            <h2 className="text-2xl font-bold">Message reçu !</h2>
            <p className="text-muted-foreground">Nous vous répondons personnellement à l&apos;adresse fournie sous 24h ouvrées.</p>
            <Button variant="outline" onClick={() => setSent(false)} className="mt-3">Envoyer un autre message</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="c-name">Nom *</Label>
                <Input id="c-name" required maxLength={100} value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jean Dupont" data-testid="contact-name-input" />
              </div>
              <div>
                <Label htmlFor="c-email">Email *</Label>
                <Input id="c-email" type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="vous@exemple.fr" data-testid="contact-email-input" />
              </div>
            </div>

            <div>
              <Label htmlFor="c-subject">Sujet *</Label>
              <select
                id="c-subject"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="w-full h-10 px-3 rounded-md bg-background border border-input text-sm"
                data-testid="contact-subject-select"
              >
                {subjects.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div>
              <Label htmlFor="c-message">Votre message *</Label>
              <Textarea id="c-message" required minLength={10} maxLength={5000} rows={7}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Décrivez votre demande ou votre problème le plus précisément possible…"
                data-testid="contact-message-textarea" />
              <div className="text-xs text-muted-foreground mt-1">{form.message.length}/5000</div>
            </div>

            {/* Honeypot — hidden from users, bots fill it */}
            <div className="hidden" aria-hidden="true">
              <label htmlFor="c-website">Website</label>
              <input id="c-website" type="text" tabIndex={-1} autoComplete="off"
                value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            </div>

            <div className="flex items-center justify-between gap-3 pt-2">
              <p className="text-xs text-muted-foreground">
                En envoyant ce message, vous acceptez que vos données soient traitées conformément à notre <a href="/legal/rgpd" className="text-primary hover:underline">politique de confidentialité</a>.
              </p>
              <Button type="submit" disabled={submitting} className="bg-primary hover:bg-primary/90 shrink-0" data-testid="contact-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Envoyer
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
